/**
 * TrendingPage — Universe "Trending today" sub-route (deepcontext-d68.8).
 *
 * Ports the velocity / spike report from `vytas-deepcontext-clusters/src/routes/Trending.jsx`
 * to TypeScript. Reads `GET /api/clusters/trending?market=<market>` for
 * volume-ranked clusters with hourly sparklines and 24h ad-request counts;
 * the Velocity lens is the top 20 of those, re-sorted by growth (last 6h
 * vs previous 6h).
 *
 * Drill-down state lives in the URL as `?cluster=<id>` so deep-linking + back
 * navigation work without a global store.
 */

import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useSearchParams} from 'react-router-dom';
import {usePortalSlot} from '../../hooks/usePortalSlot';
import {Card, CardContent} from '../../components/ui/card';
import HeroSpikeCard from './components/HeroSpikeCard';
import SecondarySpikeCard from './components/SecondarySpikeCard';
import HotUrlRow from './components/HotUrlRow';
import {useChartPalette} from '../../components/charts/useChartMode';
import ClustersMarketContext from './contexts/ClustersMarketContext';
import {formatDelta} from './utils/formatDelta';
import {
    isAbortError,
    extractApiError,
    clustersApi,
    TrendingClusterPayload,
    TrendingPayload,
    ClustersTopicDetail,
    ClustersTopUrlEntry,
    ClustersUrlRow,
} from '../../api/clusters';

/**
 * Spike-card hierarchy (bd d68.22): exactly one large hero card + two
 * secondaries beside it. The previous flat 6-card grid looked uninhabited
 * because nothing told the eye which spike was THE spike of the day.
 */
const HERO_SPIKE_CARD_COUNT = 1;
const SECONDARY_SPIKE_CARD_COUNT = 2;
const VELOCITY_CHART_SLOTS = 5;
const HOT_URL_LIMIT = 14;
const EMERGING_MAX = 20;
const EMERGING_WINDOW_MS = 24 * 60 * 60 * 1000;

interface TrendingPageProps {
    /**
     * Override the context-provided market. Production code leaves this
     * undefined and reads `selectedMarket` from `ClustersMarketContext`; tests
     * pass an explicit code to avoid having to mount the provider.
     */
    market?: string;
    /**
     * Override the resolved market label. Tests use this to assert the
     * localized title without having to mount the markets-list provider.
     * Production code leaves it undefined and reads the label off the context.
     */
    marketLabel?: string;
}

interface VelocityRowProps {
    cluster: TrendingClusterPayload;
    highlighted: boolean;
}

const VELOCITY_VIEWBOX_W = 1000;
const VELOCITY_VIEWBOX_H = 220;

function velocityPath(points: number[], min: number, max: number): {line: string; area: string} {
    const safe = points.length > 0 ? points : [0];
    const denom = Math.max(1, max - min);
    const sx = VELOCITY_VIEWBOX_W / Math.max(1, safe.length - 1);
    const sy = (VELOCITY_VIEWBOX_H - 20) / denom;
    const line = safe
        .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * sx).toFixed(2)} ${(VELOCITY_VIEWBOX_H - 10 - (p - min) * sy).toFixed(2)}`)
        .join(' ');
    const area = `${line} L ${VELOCITY_VIEWBOX_W} ${VELOCITY_VIEWBOX_H} L 0 ${VELOCITY_VIEWBOX_H} Z`;
    return {line, area};
}

/**
 * Colour for a cluster's rank position in the locked categorical order (bd
 * deepcontext-fgvn) — index 0 is always series-1 purple, index 1 series-2,
 * etc. Colour is tied to RANK (position in a velocity-ranked slice), not
 * cluster identity, matching the mockup's fixed Transfer-deadline=s1 /
 * Fuel-subsidy=s2 / … assignment. Cycles past the palette length so a 20-chip
 * emerging-topics row still resolves a colour for every chip.
 */
function seriesColorAt(categorical: string[], index: number): string {
    if (categorical.length === 0) return '#8D5DD9';
    return categorical[index % categorical.length];
}

/** Neutral dot colour for a Hot URLs row whose story has no rank to borrow
 * (not present in `fallbackClusters`) — never reuse rank 0's colour, which
 * would misleadingly imply the row belongs to the top-ranked story. */
const NO_RANK_DOT_COLOR = '#9CA3AF';

const VelocityChart: React.FC<{
    clusters: TrendingClusterPayload[];
    highlightId: string | null;
    categorical: string[];
}> = ({clusters, highlightId, categorical}) => {
    const all = clusters.flatMap((c) => c.sparkline);
    const max = all.length > 0 ? Math.max(...all) : 0;
    const min = all.length > 0 ? Math.min(...all) : 0;
    return (
        <div className="relative">
            <svg
                viewBox={`0 0 ${VELOCITY_VIEWBOX_W} ${VELOCITY_VIEWBOX_H}`}
                className="w-full h-[220px]"
                preserveAspectRatio="none"
                data-testid="trending-velocity-chart"
                role="img"
                aria-label="Topic velocity, last 24 hours"
            >
                <g stroke="#00000010" strokeDasharray="2 3">
                    <line x1="0" y1={VELOCITY_VIEWBOX_H * 0.25} x2={VELOCITY_VIEWBOX_W} y2={VELOCITY_VIEWBOX_H * 0.25} />
                    <line x1="0" y1={VELOCITY_VIEWBOX_H * 0.5} x2={VELOCITY_VIEWBOX_W} y2={VELOCITY_VIEWBOX_H * 0.5} />
                    <line x1="0" y1={VELOCITY_VIEWBOX_H * 0.75} x2={VELOCITY_VIEWBOX_W} y2={VELOCITY_VIEWBOX_H * 0.75} />
                </g>
                {clusters.map((cluster, i) => {
                    const isHi = highlightId === cluster.id;
                    const color = seriesColorAt(categorical, i);
                    const {line, area} = velocityPath(cluster.sparkline, min, max);
                    return (
                        <g key={cluster.id} opacity={highlightId && !isHi ? 0.18 : 1}>
                            {isHi && <path d={area} fill={color} fillOpacity={0.18} />}
                            <path
                                d={line}
                                stroke={color}
                                strokeWidth={isHi ? 3 : 2}
                                fill="none"
                                strokeLinecap="round"
                            />
                        </g>
                    );
                })}
            </svg>
            {/* Time-axis labels render as plain HTML text below the svg, never
                inside it — a stretched (preserveAspectRatio="none") svg
                distorts any text drawn inside its own viewBox. */}
            <div className="flex justify-between text-xs font-semibold uppercase tracking-wider text-ink-muted mt-2 px-1">
                <span>−24h</span>
                <span>−18h</span>
                <span>−12h</span>
                <span>−6h</span>
                <span>now</span>
            </div>
        </div>
    );
};

const VelocityChip: React.FC<VelocityRowProps & {onClick: () => void; color: string}> = ({
    cluster,
    highlighted,
    onClick,
    color,
}) => {
    const delta = formatDelta(cluster.delta_pct);
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`trending-velocity-chip-${cluster.id}`}
            className={`h-[42px] px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all whitespace-nowrap ${
                highlighted
                    ? 'ring-2 ring-accent bg-canvas text-accent-active'
                    : 'bg-ghost-bg text-ink hover:opacity-80'
            }`}
        >
            <span className="w-2 h-2 rounded shrink-0" style={{background: color}} />
            <span>{cluster.label}</span>
            <span className={`tabular-nums ${delta.toneClass}`}>{delta.text}</span>
        </button>
    );
};

interface LensColumnProps {
    kicker: string;
    title: string;
    items: TrendingClusterPayload[];
    metric: (c: TrendingClusterPayload) => string;
    /** Optional per-row class override for the metric span — Velocity uses
     * this to carry formatDelta's toneClass, mirroring the Emerging chips. */
    metricClassName?: (c: TrendingClusterPayload) => string;
    selectedId: string | null;
    onPick: (id: string) => void;
}

const LensColumn: React.FC<LensColumnProps> = ({
    kicker,
    title,
    items,
    metric,
    metricClassName,
    selectedId,
    onPick,
}) => (
    <div>
        <div className="text-xs font-bold uppercase tracking-widest mb-1 text-accent-active">
            {kicker}
        </div>
        <h4 className="text-xs font-extrabold text-ink mb-2 leading-tight">{title}</h4>
        <div>
            {items.map((c) => (
                <button
                    key={c.id}
                    type="button"
                    onClick={() => onPick(c.id)}
                    data-testid={`trending-lens-row-${c.id}`}
                    className={`w-full text-left flex items-center gap-2 py-1 px-1.5 -mx-1.5 rounded transition-all border-b border-line last:border-b-0 ${
                        selectedId === c.id ? 'bg-canvas' : 'hover:bg-ghost-bg'
                    }`}
                >
                    <span className="flex-1 min-w-0 text-sm font-semibold text-ink truncate">{c.label}</span>
                    <span
                        className={`font-bold text-sm shrink-0 tabular-nums ${
                            metricClassName ? metricClassName(c) : 'text-ink-muted'
                        }`}
                    >
                        {metric(c)}
                    </span>
                </button>
            ))}
        </div>
    </div>
);

function formatTimeAgo(iso: string): string {
    if (!iso) return '—';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
}

/**
 * "joined Xh ago" / "joined Xd ago" for the drilled URL list (design B,
 * bd deepcontext-k9sw4) — a long-running story's members routinely joined
 * days ago, so unlike {@link formatTimeAgo} this rolls past hours into days
 * instead of growing an unreadable hour count.
 */
function formatJoinedAgo(iso: string): string {
    if (!iso) return 'joined —';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'joined just now';
    if (minutes < 60) return `joined ${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `joined ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `joined ${days}d ago`;
}

function HotUrlsFeed({
    drillCluster,
    drillCluster_topUrls,
    drillCluster_loading,
    drillCluster_error,
    fallbackClusters,
    hotUrls,
    hotUrlsLoading,
    hotUrlsError,
    marketLabel,
    categorical,
}: {
    drillCluster: TrendingClusterPayload | null;
    drillCluster_topUrls: ClustersTopUrlEntry[];
    drillCluster_loading: boolean;
    drillCluster_error: string | null;
    fallbackClusters: TrendingClusterPayload[];
    /** Market-wide URLs from `GET /api/clusters/urls` (no cluster filter). */
    hotUrls: ClustersUrlRow[];
    hotUrlsLoading: boolean;
    hotUrlsError: string | null;
    marketLabel: string;
    /** Theme's categorical palette — dots key off rank, not identity. */
    categorical: string[];
}) {
    if (drillCluster) {
        if (drillCluster_loading) {
            return (
                <div
                    data-testid="trending-hoturls-loading"
                    className="text-sm text-ink-muted py-8 text-center"
                >
                    Loading top URLs…
                </div>
            );
        }
        if (drillCluster_error) {
            return (
                <div
                    data-testid="trending-hoturls-error"
                    className="text-sm text-risk py-8 text-center"
                >
                    {drillCluster_error}
                </div>
            );
        }
        if (drillCluster_topUrls.length === 0) {
            return (
                <div
                    data-testid="trending-hoturls-empty"
                    className="text-sm text-ink-muted py-8 text-center"
                >
                    No URLs yet for {drillCluster.label}.
                </div>
            );
        }
        const rank = Math.max(0, fallbackClusters.findIndex((c) => c.id === drillCluster.id));
        const dotColor = seriesColorAt(categorical, rank);
        return (
            <ul data-testid="trending-drill-urls" className="divide-y divide-line">
                {drillCluster_topUrls.slice(0, HOT_URL_LIMIT).map((u) => (
                    <HotUrlRow
                        key={u.url_hash}
                        dotColor={dotColor}
                        title={u.title}
                        url={u.url}
                        urlHash={u.url_hash}
                        score={u.sim_event}
                        countToday={u.bid_requests_today}
                        joinedLabel={formatJoinedAgo(u.joined_at)}
                    />
                ))}
            </ul>
        );
    }

    // No cluster picked — market-wide feed from GET /api/clusters/urls.
    if (hotUrlsLoading) {
        return (
            <div
                data-testid="trending-hoturls-loading"
                className="text-sm text-ink-muted py-8 text-center"
            >
                Loading top URLs…
            </div>
        );
    }
    if (hotUrlsError) {
        return (
            <div
                data-testid="trending-hoturls-error"
                className="text-sm text-risk py-8 text-center"
            >
                {hotUrlsError}
            </div>
        );
    }
    if (hotUrls.length === 0) {
        return (
            <div
                data-testid="trending-hoturls-empty"
                className="text-sm text-ink-muted py-8 text-center"
            >
                No URLs yet for {marketLabel}.
            </div>
        );
    }
    return (
        <ul data-testid="trending-hoturls-feed" className="divide-y divide-line">
            {hotUrls.slice(0, HOT_URL_LIMIT).map((row) => {
                // `fallbackClusters[].id` is `String(story.id)` — see
                // `toLegacyTrendingCluster` in
                // `backend/src/controllers/clustersController.ts`, which the
                // adapted `/api/clusters/trending` response always goes
                // through. A row whose story fell out of `fallbackClusters`
                // (e.g. not in today's trending set) has no rank to borrow —
                // don't silently collapse that to rank 0's colour (bd
                // deepcontext-b3to.8 review), fall back to a neutral dot.
                const rankIndex = fallbackClusters.findIndex(
                    (c) => c.id === String(row.story_id),
                );
                const dotColor =
                    rankIndex === -1
                        ? NO_RANK_DOT_COLOR
                        : seriesColorAt(categorical, rankIndex);
                return (
                    <HotUrlRow
                        key={row.url_hash}
                        dotColor={dotColor}
                        title={row.title}
                        url={row.url}
                        urlHash={row.url_hash}
                        score={row.sim_event}
                        timeAgo={formatTimeAgo(row.joined_at)}
                    />
                );
            })}
        </ul>
    );
}

/**
 * deepcontext-ctk6 — header caption row.
 *
 * Same 10px uppercase caption `CaptionedField`'s `CaptionLabel` draws above
 * the Market selector, so the "Live · {market}" eyebrow lands on exactly the
 * same line as the "Market" caption. Kept as a class string here rather than
 * reusing `CaptionLabel` because this row swaps the static dot for the live
 * pulse dot.
 */
const CAPTION_ROW_CLASSES =
    'flex items-center gap-[7px] pl-1.5 mb-[7px] text-[10px] font-bold uppercase leading-none tracking-[.14em] text-accent-active dark:text-accent';

/**
 * deepcontext-6c8w — same rhythm as `CAPTION_ROW_CLASSES`, but without the
 * `pl-1.5` that `CaptionLabel` needs to line up over the bordered Market
 * pill. The Live caption sits directly above the (unpadded) h2 title, so its
 * pulse dot must start flush with the title's left edge instead.
 */
const LIVE_CAPTION_ROW_CLASSES = CAPTION_ROW_CLASSES.replace('pl-1.5 ', '');

/**
 * Caption block height: a 10px `leading-none` line plus its 7px gap — the
 * same rhythm `CaptionLabel` uses above the Market selector.
 */
const CAPTION_BLOCK_HEIGHT_PX = 17;

/**
 * One Clusters / Window / Refreshed stat in the page-header actions row.
 *
 * The caption stays IN FLOW so it sizes the column (an absolutely positioned
 * caption is wider than a value like "6" and overlaps its neighbour). The
 * negative top margin then shrinks the flex item's MARGIN box back to the
 * 42px value box, which is what `#app-page-actions` (an `items-center` row)
 * aligns on — so the value lands on the same baseline as the URL-count badge
 * and the Market pill while the caption rides above, on the caption line.
 */
const HeaderStat: React.FC<{label: string; value: string; testId: string}> = ({
    label,
    value,
    testId,
}) => (
    <div className="flex flex-col" style={{marginTop: -CAPTION_BLOCK_HEIGHT_PX}}>
        <div className="mb-[7px] text-[10px] font-bold uppercase leading-none tracking-[.14em] text-ink-muted whitespace-nowrap">
            {label}
        </div>
        <div
            data-testid={testId}
            className="h-[42px] flex items-center text-sm font-extrabold text-ink tabular-nums whitespace-nowrap"
        >
            {value}
        </div>
    </div>
);

const TrendingPage: React.FC<TrendingPageProps> = ({
    market: marketProp,
    marketLabel: marketLabelProp,
}) => {
    const ctx = useContext(ClustersMarketContext);
    const market = marketProp ?? ctx?.selectedMarket ?? null;
    /**
     * Resolved label for the selected market. Pulled from the
     * markets list that the same context populates from
     * `GET /api/clusters/markets` (the data source the language dropdown
     * also reads from). Falls back to the bare code so the heading still
     * renders something readable when the markets list hasn't landed yet.
     */
    const marketLabel =
        marketLabelProp ??
        ctx?.markets.find((m) => m.code === market)?.label ??
        market ??
        '—';
    // Theme-aware categorical palette (bd deepcontext-fgvn) — re-read on every
    // light/dark toggle so every hand-rendered chart on this page repaints.
    const chartPalette = useChartPalette();
    // deepcontext-ctk6: the page header lives in AppLayout's shared header
    // row, not in this page's body — eyebrow + title on the left, beside the
    // captioned Market selector `ClustersTopBar` portals there; the stat trio
    // on the right, ahead of the URL-count badge.
    const contextSlot = usePortalSlot('app-page-context');
    const actionsSlot = usePortalSlot('app-page-actions');
    const [payload, setPayload] = useState<TrendingPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const drillId = searchParams.get('cluster');

    const [drillDetail, setDrillDetail] = useState<ClustersTopicDetail | null>(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillError, setDrillError] = useState<string | null>(null);

    const [hotUrls, setHotUrls] = useState<ClustersUrlRow[]>([]);
    const [hotUrlsLoading, setHotUrlsLoading] = useState(true);
    const [hotUrlsError, setHotUrlsError] = useState<string | null>(null);

    // Cancel the trending fetch when the user switches tabs / leaves Universe
    // (bd deepcontext-26s).
    useEffect(() => {
        if (!market) {
            setLoading(true);
            return;
        }
        const controller = new AbortController();
        setLoading(true);
        setLoadError(null);
        clustersApi
            .getTrending(market, {signal: controller.signal})
            .then((data) => {
                if (controller.signal.aborted) return;
                setPayload(data);
            })
            .catch((err: unknown) => {
                if (isAbortError(err) || controller.signal.aborted) return;
                setLoadError(extractApiError(err, 'Failed to load Trending.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [market]);

    // Market-wide Hot URLs feed for the undrilled state (bd deepcontext-isul)
    // — mirrors the trending-fetch effect above, but hits the URL Explorer
    // endpoint with no cluster filter so the panel shows real per-URL rows
    // instead of synthesizing them from cluster-level velocity.
    useEffect(() => {
        if (!market) {
            setHotUrlsLoading(true);
            return;
        }
        const controller = new AbortController();
        setHotUrlsLoading(true);
        setHotUrlsError(null);
        clustersApi
            .fetchClustersUrls({market, sort: 'recent'}, {signal: controller.signal})
            .then((data) => {
                if (controller.signal.aborted) return;
                setHotUrls(data.rows ?? []);
            })
            .catch((err: unknown) => {
                if (isAbortError(err) || controller.signal.aborted) return;
                setHotUrlsError(extractApiError(err, 'Failed to load Hot URLs.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setHotUrlsLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [market]);

    // Ranked by volume then growth (spec section 10): ties on 24h volume
    // break on growth, highest first.
    const byVolume = useMemo<TrendingClusterPayload[]>(() => {
        if (!payload) return [];
        return [...payload.clusters].sort(
            (a, b) =>
                b.current_count - a.current_count ||
                (b.delta_pct ?? -Infinity) - (a.delta_pct ?? -Infinity),
        );
    }, [payload]);
    // Velocity is the top 20 by volume, re-sorted by growth (delta_pct desc,
    // null last) — a low-volume story can't spike off a tiny base and outrank
    // the stories people actually read (bd deepcontext-pkyo.2).
    const byVelocity = useMemo<TrendingClusterPayload[]>(() => {
        return byVolume.slice(0, 20).sort(
            (a, b) => (b.delta_pct ?? -Infinity) - (a.delta_pct ?? -Infinity),
        );
    }, [byVolume]);
    // Emerging = stories first seen within the last 24h, ranked by volume
    // (current_count desc) — distinct from Velocity's growth-sorted list
    // (bd deepcontext-pkyo.3).
    const emerging = useMemo<TrendingClusterPayload[]>(() => {
        if (!payload) return [];
        const cutoffMs = Date.now() - EMERGING_WINDOW_MS;
        return payload.clusters
            .filter((c) => new Date(c.first_seen).getTime() >= cutoffMs)
            .sort((a, b) => b.current_count - a.current_count)
            .slice(0, EMERGING_MAX);
    }, [payload]);
    const drillCluster = useMemo<TrendingClusterPayload | null>(() => {
        if (!drillId || !payload) return null;
        return payload.clusters.find((c) => c.id === drillId) ?? null;
    }, [drillId, payload]);

    // Fetch story detail for the drilled cluster. `drillCluster.id` IS the
    // story id (the legacy-shaped payload stringifies it — see
    // `toLegacyTrendingCluster` in the backend controller), so this drills
    // straight into the topic endpoint. The fetch is aborted if the user
    // picks a different cluster or unmounts before the topic call resolves
    // (bd deepcontext-26s).
    useEffect(() => {
        if (!drillCluster) {
            setDrillDetail(null);
            setDrillError(null);
            // bd-7t6di: clear the spinner when there is no drilled cluster.
            // Without this, switching from a topic-bearing cluster (which set
            // drillLoading=true) to no cluster leaves the spinner stuck on
            // "Loading top URLs…" forever.
            setDrillLoading(false);
            return;
        }
        const controller = new AbortController();
        setDrillLoading(true);
        setDrillError(null);
        clustersApi
            .getTopic(drillCluster.id, {signal: controller.signal})
            .then((detail) => {
                if (controller.signal.aborted) return;
                setDrillDetail(detail);
            })
            .catch((err: unknown) => {
                if (isAbortError(err) || controller.signal.aborted) return;
                setDrillError(extractApiError(err, 'Failed to load drill-down.'));
            })
            .finally(() => {
                if (!controller.signal.aborted) setDrillLoading(false);
            });
        return () => {
            controller.abort();
        };
    }, [drillCluster]);

    const setDrill = useCallback(
        (id: string | null) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (id) next.set('cluster', id);
                    else next.delete('cluster');
                    return next;
                },
                {replace: true},
            );
        },
        [setSearchParams],
    );

    const handleSelect = useCallback(
        (id: string) => {
            if (drillId === id) setDrill(null);
            else setDrill(id);
        },
        [drillId, setDrill],
    );

    const handleClearDrill = useCallback(() => setDrill(null), [setDrill]);

    if (loading) {
        return (
            <div
                data-testid="trending-loading"
                className="text-sm text-ink-muted py-12 text-center"
            >
                Loading Trending…
            </div>
        );
    }
    if (loadError) {
        return (
            <div
                data-testid="trending-load-error"
                className="text-sm text-risk py-12 text-center"
            >
                {loadError}
            </div>
        );
    }
    if (!payload || byVelocity.length === 0) {
        return (
            <div
                data-testid="trending-empty"
                className="text-sm text-ink-muted py-12 text-center"
            >
                No trending clusters yet for market {market ?? '—'}.
            </div>
        );
    }

    // Hero + secondaries are ranked by 24h volume, not spike size (bd
    // deepcontext-ws2b.3 / Vytas 2026-09-01): "the trend is what people are
    // reading right now", i.e. bid-request volume, not a low-base spike.
    const heroCluster = byVolume[0] ?? null;
    const secondaryClusters = byVolume.slice(
        HERO_SPIKE_CARD_COUNT,
        HERO_SPIKE_CARD_COUNT + SECONDARY_SPIKE_CARD_COUNT,
    );
    const velocityChartClusters = byVelocity.slice(0, VELOCITY_CHART_SLOTS);
    const refreshedLabel = formatTimeAgo(payload.generated_at);
    // While the hourly snapshot job is still warming up, growth % is computed
    // from a partial window and is misleading — hide it and say why.
    const showGrowth = payload.snapshot_hours >= 24;

    return (
        /* deepcontext-ctk6: full-bleed now (no CardContent wrapper), so this
           root owns the scroll the Card's content box used to provide. */
        <div
            data-testid="trending-page"
            className="space-y-4 flex-1 min-h-0 overflow-auto"
        >
            {contextSlot &&
                createPortal(
                    <div className="min-w-0 flex-1" data-testid="trending-header-title">
                        <div className={LIVE_CAPTION_ROW_CLASSES}>
                            <span
                                data-testid="trending-pulse-dot"
                                aria-hidden
                                className="w-[5px] h-[5px] rounded-full bg-current flex-shrink-0 trending-pulse-dot"
                            />
                            Live · {marketLabel}
                        </div>
                        <h2
                            data-testid="trending-title"
                            className="text-2xl font-bold text-ink leading-[42px] truncate"
                        >
                            What {marketLabel} is talking about today
                        </h2>
                    </div>,
                    contextSlot,
                )}
            {actionsSlot &&
                createPortal(
                    /* `order-first` puts the trio left of the URL-count badge:
                       `ClustersTopBar` portals that badge into this same slot
                       and mounts first, so DOM order alone would render it
                       ahead of us (same portal-ordering trick as AnchorBar's
                       `order-last` actions block). */
                    <div
                        data-testid="trending-header-stats"
                        className="order-first flex items-end gap-6"
                    >
                        <HeaderStat
                            label="Clusters"
                            testId="trending-stat-clusters"
                            value={payload.clusters.length.toLocaleString('en-US')}
                        />
                        <HeaderStat label="Window" testId="trending-stat-window" value="24h" />
                        <HeaderStat
                            label="Refreshed"
                            testId="trending-stat-refreshed"
                            value={refreshedLabel}
                        />
                    </div>,
                    actionsSlot,
                )}

            {/* Spike cards — 1 hero + up to 2 secondaries (bd d68.22). */}
            <section
                data-testid="trending-spike-cards"
                className="grid gap-3.5 lg:grid-cols-[1.6fr_1fr]"
            >
                {heroCluster && (
                    <HeroSpikeCard
                        cluster={heroCluster}
                        selected={drillId === heroCluster.id}
                        onSelect={handleSelect}
                        showGrowth={showGrowth}
                    />
                )}
                {secondaryClusters.length > 0 && (
                    <div className="grid gap-3.5 grid-rows-2">
                        {secondaryClusters.map((cluster, idx) => (
                            <SecondarySpikeCard
                                key={cluster.id}
                                cluster={cluster}
                                rank={idx + 2}
                                selected={drillId === cluster.id}
                                onSelect={handleSelect}
                                showGrowth={showGrowth}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Two-lens panel */}
            <Card>
                <CardContent className="p-6">
                    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                        <LensColumn
                            kicker="Volume"
                            title="Most requested · last 24h"
                            items={byVolume.slice(0, 5)}
                            metric={(c) => c.current_count.toLocaleString()}
                            selectedId={drillId}
                            onPick={handleSelect}
                        />
                        
                        <LensColumn
                            kicker="Velocity"
                            title="Rising · last 6h vs previous 6h"
                            items={byVelocity.slice(0, 5)}
                            metric={(c) => formatDelta(c.delta_pct).text}
                            metricClassName={(c) => formatDelta(c.delta_pct).toneClass}
                            selectedId={drillId}
                            onPick={handleSelect}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Velocity chart */}
            <Card>
                <CardContent className="p-6">
                    <div className="flex flex-col gap-3 mb-3">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest text-ink-muted">
                                Topic velocity · last 24 hours
                            </div>
                            <h3 className="text-lg font-bold text-ink">Rising and falling, hour by hour</h3>
                        </div>
                        {!showGrowth && (
                            <div
                                data-testid="trending-warmup-notice"
                                className="text-xs font-semibold text-ink-muted bg-ghost-bg rounded-lg px-3 py-2 w-fit"
                            >
                                Collecting hourly data — {payload.snapshot_hours} of 24 hours so far
                            </div>
                        )}
                        <div className="flex gap-1.5 flex-wrap">
                            {velocityChartClusters.map((c, i) => (
                                <VelocityChip
                                    key={c.id}
                                    cluster={c}
                                    highlighted={drillId === c.id}
                                    onClick={() => handleSelect(c.id)}
                                    color={seriesColorAt(chartPalette.categorical, i)}
                                />
                            ))}
                        </div>
                    </div>
                    <VelocityChart
                        clusters={velocityChartClusters}
                        highlightId={drillId}
                        categorical={chartPalette.categorical}
                    />
                </CardContent>
            </Card>

            {/* Emerging topics + Hot URLs feed */}
            <div className="grid gap-3 grid-cols-1 lg:grid-cols-5">
                <Card className="lg:col-span-2">
                    <CardContent className="p-6">
                        <div className="text-xs font-bold uppercase tracking-widest text-ink-muted">
                            Emerging topics
                        </div>
                        <h3 className="text-lg font-bold text-ink mb-3">
                            Clusters surfacing today
                        </h3>
                        {emerging.length === 0 ? (
                            <div className="text-sm text-ink-muted">
                                No new stories in the last 24 hours.
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5" data-testid="trending-emerging">
                                {emerging.map((c, i) => {
                                    const delta = formatDelta(c.delta_pct);
                                    const color = seriesColorAt(chartPalette.categorical, i);
                                    return (
                                        <button
                                            key={c.id}
                                            type="button"
                                            onClick={() => handleSelect(c.id)}
                                            data-testid={`trending-emerging-${c.id}`}
                                            className={`h-[42px] px-3.5 rounded-full text-xs font-semibold transition-all flex items-center gap-1.5 text-ink ${
                                                drillId === c.id ? 'ring-2 ring-accent' : 'hover:opacity-80'
                                            }`}
                                            style={{background: `color-mix(in srgb, ${color} 16%, transparent)`}}
                                        >
                                            <span>{c.label}</span>
                                            <span className={`font-extrabold tabular-nums ${delta.toneClass}`}>
                                                {delta.text}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card className="lg:col-span-3">
                    <CardContent className="p-6 flex flex-col min-h-[420px]">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-widest text-ink-muted">
                                    {drillCluster ? `Drilled into · ${drillCluster.label}` : 'Hot URLs · today'}
                                </div>
                                <h3 className="text-lg font-bold text-ink">
                                    {drillCluster ? 'Top URLs · ad requests today' : 'What just got picked up'}
                                </h3>
                            </div>
                            {drillCluster && (
                                <button
                                    type="button"
                                    onClick={handleClearDrill}
                                    data-testid="trending-drill-clear"
                                    className="text-xs font-semibold text-accent hover:underline shrink-0"
                                >
                                    ← Back to today&apos;s feed
                                </button>
                            )}
                        </div>
                        <div className="flex-1 overflow-auto">
                            <HotUrlsFeed
                                drillCluster={drillCluster}
                                drillCluster_topUrls={drillDetail?.members ?? []}
                                drillCluster_loading={drillLoading}
                                drillCluster_error={drillError}
                                fallbackClusters={byVelocity}
                                hotUrls={hotUrls}
                                hotUrlsLoading={hotUrlsLoading}
                                hotUrlsError={hotUrlsError}
                                marketLabel={marketLabel}
                                categorical={chartPalette.categorical}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default TrendingPage;
